import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api, apiErr, STATUS_LABELS, ROLE_LABELS } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { MetricCards, TargetActualChart, RiskBadge, StaffProductivityCard, TargetHistoryCard, ArchiveStatusCard } from "@/components/ProjectMetrics";
import ReviewPanel from "@/components/ReviewPanel";
import ApprovalQueue from "@/pages/ApprovalQueue";
import DaftarArsip from "@/pages/DaftarArsip";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ArrowLeft, UploadCloud, RefreshCw, FolderSync, CheckCircle2, Play, Eye, Trash2, UserPlus, FileSpreadsheet, Download, Loader2, Database, History, AlertCircle, CheckCircle,
} from "lucide-react";

const DOC_STATUS = {
  queued: { label: "Antri", cls: "bg-slate-500/15 text-slate-600" },
  processing: { label: "Diproses AI", cls: "bg-red-500/15 text-red-600" },
  processed: { label: "Selesai", cls: "bg-emerald-500/15 text-emerald-600" },
  error: { label: "Error", cls: "bg-red-500/15 text-red-600" },
};

export default function ProjectDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [p, setP] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [ef, setEf] = useState(null);
  const [savingEdit, setSavingEdit] = useState(false);

  const load = useCallback(() => api.get(`/projects/${id}`).then((r) => setP(r.data)).catch((e) => { toast.error(apiErr(e)); nav("/projects"); }), [id]);
  useEffect(() => { load(); }, [load]);

  if (!p) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-32" /><Skeleton className="h-80" /></div>;

  const isPimpinan = ["PIMPINAN_PROYEK", "MANAGER_OPERASIONAL"].includes(user.role) && p.pimpinan_id === user.id;
  const canManage = ["SUPER_ADMIN", "MANAGER_OPERASIONAL"].includes(user.role) || isPimpinan;
  const canMaster = canManage;
  const isStaffOnProject = (p.staff_ids || []).includes(user.id);
  const canProcess = ["SUPER_ADMIN", "STAFF_OPERASIONAL"].includes(user.role) || isPimpinan || isStaffOnProject;
  const canComplete = user.role === "SUPER_ADMIN" || isPimpinan;
  const canDelete = ["SUPER_ADMIN", "MANAGER_OPERASIONAL"].includes(user.role);

  const openEdit = () => {
    setEf({
      name: p.name || "", client: p.client || "", location: p.location || "",
      start_date: (p.start_date || "").slice(0, 10), target_completion_date: (p.target_completion_date || "").slice(0, 10),
      total_volume_ml: p.total_volume_ml ?? 0, estimated_volume: p.estimated_volume ?? 0, working_days: p.working_days ?? 0,
      bidang_list: (p.bidang_list || []).join(", "), status: p.status, notes: p.notes || "",
    });
    setEditOpen(true);
  };
  const saveEdit = async () => {
    setSavingEdit(true);
    try {
      const payload = { ...ef, total_volume_ml: +ef.total_volume_ml, estimated_volume: +ef.estimated_volume, working_days: +ef.working_days,
        bidang_list: ef.bidang_list.split(",").map((s) => s.trim()).filter(Boolean) };
      await api.patch(`/projects/${id}`, payload);
      toast.success("Proyek diperbarui"); setEditOpen(false); load();
    } catch (e) { toast.error(apiErr(e)); } finally { setSavingEdit(false); }
  };
  const removeProject = async () => {
    if (!window.confirm("Hapus proyek ini secara permanen? Semua berkas, arsip, dan riwayat di dalamnya akan dihapus total dan tidak dapat dipulihkan.")) return;
    try { await api.delete(`/projects/${id}`); toast.success("Proyek dihapus"); nav("/projects"); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const complete = async () => {
    try { await api.post(`/projects/${id}/complete`); toast.success("Proyek diselesaikan"); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  return (
    <div className="space-y-6">
      <button onClick={() => nav("/projects")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors" data-testid="back-btn">
        <ArrowLeft className="h-4 w-4" /> Kembali ke Proyek
      </button>

      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="flex items-center gap-2">
            <p className="font-mono text-xs text-primary font-semibold">{p.code}</p>
            <Badge variant="secondary">{STATUS_LABELS[p.status]}</Badge>
            <RiskBadge risk={p.metrics.risk} />
          </div>
          <h1 className="font-heading text-3xl font-bold tracking-tight mt-1">{p.name}</h1>
          <p className="text-sm text-muted-foreground">{p.client} · {p.location || "-"} · Pimpinan: {p.pimpinan_name || "belum ditugaskan"}</p>
        </div>
        {(canComplete || canManage || canDelete) && (
          <div className="flex items-center gap-2 flex-wrap">
            {canManage && p.status !== "COMPLETED" && (
              <Button variant="outline" onClick={openEdit} className="rounded-full" data-testid="edit-project-btn">
                <History className="h-4 w-4 mr-1.5" /> Edit Proyek
              </Button>
            )}
            {canComplete && p.status !== "COMPLETED" && (
              <Button onClick={complete} className="rounded-full bg-emerald-600 hover:bg-emerald-700" data-testid="complete-project-btn">
                <CheckCircle2 className="h-4 w-4 mr-1.5" /> Selesaikan Proyek
              </Button>
            )}
            {canDelete && (
              <Button variant="outline" onClick={removeProject} className="rounded-full text-red-600 border-red-200 hover:bg-red-50" data-testid="delete-project-btn">
                <Trash2 className="h-4 w-4 mr-1.5" /> Hapus
              </Button>
            )}
          </div>
        )}
      </div>

      <MetricCards m={p.metrics} />

      <Tabs defaultValue="overview">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="overview" data-testid="tab-overview">Ringkasan</TabsTrigger>
          {canMaster && <TabsTrigger value="master" data-testid="tab-master">Master Data</TabsTrigger>}
          {canManage && <TabsTrigger value="team" data-testid="tab-team">Tim</TabsTrigger>}
          {canProcess && <TabsTrigger value="processing" data-testid="tab-processing">Pemrosesan Arsip</TabsTrigger>}
          {canProcess && <TabsTrigger value="approval" data-testid="tab-approval">Approval Queue</TabsTrigger>}
          <TabsTrigger value="register" data-testid="tab-register">Daftar Arsip</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-4 space-y-4">
          <TargetActualChart m={p.metrics} project={p} />
          <ArchiveStatusCard projectId={id} />
          {(canManage || user.role === "DIREKTUR") && <StaffProductivityCard projectId={id} />}
          {canManage && <TargetHistoryCard projectId={id} />}
        </TabsContent>

        {canMaster && <TabsContent value="master" className="mt-4"><MasterData projectId={id} /></TabsContent>}
        {canManage && <TabsContent value="team" className="mt-4"><Team p={p} reload={load} user={user} /></TabsContent>}
        {canProcess && <TabsContent value="processing" className="mt-4"><Processing projectId={id} reload={load} /></TabsContent>}
        {canProcess && <TabsContent value="approval" className="mt-4"><ApprovalQueue projectId={id} embedded bidangOptions={p.bidang_list || []} /></TabsContent>}
        <TabsContent value="register" className="mt-4"><DaftarArsip projectId={id} embedded /></TabsContent>
      </Tabs>

      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto scrollbar-thin">
          <DialogHeader><DialogTitle className="font-heading">Edit Proyek</DialogTitle></DialogHeader>
          {ef && (
            <div className="grid sm:grid-cols-2 gap-4 py-2">
              <div className="space-y-2"><Label>Nama Proyek</Label><Input value={ef.name} onChange={(e) => setEf({ ...ef, name: e.target.value })} data-testid="edit-name" /></div>
              <div className="space-y-2"><Label>Klien / Instansi</Label><Input value={ef.client} onChange={(e) => setEf({ ...ef, client: e.target.value })} data-testid="edit-client" /></div>
              <div className="space-y-2"><Label>Lokasi</Label><Input value={ef.location} onChange={(e) => setEf({ ...ef, location: e.target.value })} data-testid="edit-location" /></div>
              <div className="space-y-2"><Label>Status</Label>
                <Select value={ef.status} onValueChange={(v) => setEf({ ...ef, status: v })}>
                  <SelectTrigger data-testid="edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{["DRAFT", "ACTIVE", "PROCESSING", "VERIFICATION", "COMPLETED", "ARCHIVED"].map((s) => <SelectItem key={s} value={s}>{STATUS_LABELS[s] || s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2"><Label>Tanggal Mulai</Label><Input type="date" value={ef.start_date} onChange={(e) => setEf({ ...ef, start_date: e.target.value })} data-testid="edit-start" /></div>
              <div className="space-y-2"><Label>Target Selesai</Label><Input type="date" value={ef.target_completion_date} onChange={(e) => setEf({ ...ef, target_completion_date: e.target.value })} data-testid="edit-target" /></div>
              <div className="space-y-2"><Label>Target Volume (mL)</Label><Input type="number" step="0.01" value={ef.total_volume_ml} onChange={(e) => setEf({ ...ef, total_volume_ml: e.target.value })} data-testid="edit-volume-ml" /></div>
              <div className="space-y-2"><Label>Estimasi Berkas</Label><Input type="number" value={ef.estimated_volume} onChange={(e) => setEf({ ...ef, estimated_volume: e.target.value })} placeholder="Contoh: 3000" data-testid="edit-estimated-volume" /></div>
              <div className="space-y-2"><Label>Hari Kerja</Label><Input type="number" value={ef.working_days} onChange={(e) => setEf({ ...ef, working_days: e.target.value })} data-testid="edit-workdays" /></div>
              <div className="sm:col-span-2 space-y-2"><Label>Daftar Bidang (pisahkan dengan koma)</Label><Input value={ef.bidang_list} onChange={(e) => setEf({ ...ef, bidang_list: e.target.value })} placeholder="Kepegawaian, Keuangan, Umum" data-testid="edit-bidang" /></div>
              <div className="sm:col-span-2 space-y-2"><Label>Catatan</Label><Textarea value={ef.notes} onChange={(e) => setEf({ ...ef, notes: e.target.value })} data-testid="edit-notes" /></div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditOpen(false)}>Batal</Button>
            <Button onClick={saveEdit} disabled={savingEdit || !ef?.name} data-testid="edit-submit">{savingEdit ? "Menyimpan..." : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function MasterData({ projectId }) {
  const [md, setMd] = useState(null);
  const [importing, setImporting] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const [wiz, setWiz] = useState(null);
  const [wBusy, setWBusy] = useState(false);

  const load = useCallback(() => api.get(`/projects/${projectId}/master-data/unified`).then((r) => setMd(r.data)).catch(() => setMd(null)), [projectId]);
  useEffect(() => { load(); }, [load]);

  const template = async () => {
    const r = await api.get(`/projects/master-data/unified-template`, { responseType: "blob" });
    dl(r.data, "template_master_data.xlsx");
  };
  const doPreview = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setImporting(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post(`/projects/${projectId}/master-data/unified/preview`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setWiz({ ...data, mapping: { ...data.mapping } });
    } catch (err) { toast.error(apiErr(err)); } finally { setImporting(false); e.target.value = ""; }
  };
  const reanalyze = async (sheet, mapping) => {
    setWBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/master-data/unified/analyze`, { staging_id: wiz.staging_id, sheet, mapping });
      setWiz({ ...data, mapping: { ...data.mapping } });
    } catch (err) { toast.error(apiErr(err)); } finally { setWBusy(false); }
  };
  const confirmImport = async () => {
    setWBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/master-data/unified/confirm`, { staging_id: wiz.staging_id, sheet: wiz.sheet, mapping: wiz.mapping });
      setLastImport({ inserted: data.inserted, error_count: data.invalid, errors: data.errors });
      toast.success(`Import: ${data.inserted} valid, ${data.invalid} ditolak`);
      setWiz(null); load();
    } catch (err) { toast.error(apiErr(err)); } finally { setWBusy(false); }
  };

  const cols = md?.columns || [];
  return (
    <div className="space-y-4">
      <Card className="p-4 bg-accent/30">
        <p className="text-sm font-semibold flex items-center gap-1.5"><Database className="h-4 w-4 text-primary" /> Master Data Terpadu — Template Tunggal</p>
        <p className="text-xs text-muted-foreground mt-1">Satu file Excel berisi Kode Klasifikasi, Jadwal Retensi Arsip (JRA), dan Keamanan &amp; Akses. Unduh template, isi, lalu import sekali jalan.</p>
      </Card>

      <div className="flex items-center gap-3 flex-wrap">
        <Button variant="outline" onClick={template} className="rounded-full" data-testid="master-template-btn"><Download className="h-4 w-4 mr-1.5" /> Template</Button>
        <label>
          <input type="file" accept=".xlsx,.xls" className="hidden" onChange={doPreview} data-testid="master-import-input" />
          <Button asChild className="rounded-full" disabled={importing}><span>{importing ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <FileSpreadsheet className="h-4 w-4 mr-1.5" />} Import Excel</span></Button>
        </label>
        {md && <Badge variant="secondary">{md.items.length} kode klasifikasi</Badge>}
      </div>

      {lastImport && (
        <Card className={`p-4 border-l-4 ${lastImport.error_count > 0 ? "border-l-amber-500" : "border-l-emerald-500"}`} data-testid="import-result">
          <div className="flex items-center gap-2">
            {lastImport.error_count > 0 ? <AlertCircle className="h-4 w-4 text-amber-500" /> : <CheckCircle className="h-4 w-4 text-emerald-500" />}
            <p className="text-sm font-semibold">Hasil Import: {lastImport.inserted} record masuk, {lastImport.error_count} ditolak</p>
          </div>
          {lastImport.errors?.length > 0 && (
            <div className="mt-2 max-h-40 overflow-y-auto scrollbar-thin space-y-1">
              {lastImport.errors.map((er, i) => (<p key={i} className="text-xs text-amber-600">Baris {er.row}: {er.error}</p>))}
            </div>
          )}
        </Card>
      )}

      <Card className="overflow-hidden">
        {!md ? <div className="p-6"><Skeleton className="h-8 w-full" /></div> : md.items.length === 0 ? (
          <div className="p-12 text-center text-muted-foreground"><Database className="h-8 w-8 mx-auto mb-2" />Belum ada master data. Unduh template lalu import.</div>
        ) : (
          <div className="overflow-x-auto scrollbar-thin">
            <Table>
              <TableHeader><TableRow className="bg-muted/50">{cols.map((c) => <TableHead key={c} className="text-xs whitespace-nowrap">{c}</TableHead>)}</TableRow></TableHeader>
              <TableBody>{md.items.map((it, i) => <TableRow key={it.kode_klasifikasi || i}>{cols.map((c) => <TableCell key={c} className="text-sm">{it[c]}</TableCell>)}</TableRow>)}</TableBody>
            </Table>
          </div>
        )}
      </Card>

      <MasterImportWizard wiz={wiz} setWiz={setWiz} busy={wBusy} onReanalyze={reanalyze} onConfirm={confirmImport} />
    </div>
  );
}

function MasterImportWizard({ wiz, setWiz, busy, onReanalyze, onConfirm }) {
  if (!wiz) return null;
  const cols = wiz.required_columns || [];
  const headers = wiz.headers || [];
  const setMap = (c, v) => setWiz({ ...wiz, mapping: { ...wiz.mapping, [c]: v === "none" ? null : Number(v) } });
  return (
    <Dialog open={!!wiz} onOpenChange={(o) => !o && setWiz(null)}>
      <DialogContent className="max-w-4xl max-h-[92vh] overflow-y-auto scrollbar-thin">
        <DialogHeader><DialogTitle className="font-heading">Pratinjau Import — {wiz.filename}</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            {wiz.worksheets?.length > 1 && (
              <div className="space-y-1">
                <p className="text-xs font-semibold">Worksheet</p>
                <Select value={wiz.sheet} onValueChange={(v) => onReanalyze(v, wiz.mapping)}>
                  <SelectTrigger className="w-52" data-testid="wiz-sheet"><SelectValue /></SelectTrigger>
                  <SelectContent>{wiz.worksheets.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div className="flex items-center gap-3 text-sm ml-auto">
              <Badge className="bg-emerald-500/15 text-emerald-600 border-emerald-500/30" variant="outline" data-testid="wiz-valid-count">{wiz.valid} valid</Badge>
              <Badge className="bg-red-500/15 text-red-600 border-red-500/30" variant="outline" data-testid="wiz-invalid-count">{wiz.invalid} ditolak</Badge>
              <span className="text-muted-foreground">dari {wiz.total_rows} baris</span>
            </div>
          </div>

          <div>
            <p className="text-xs font-semibold mb-2">Pemetaan Kolom {wiz.unmapped?.length > 0 && <span className="text-amber-600">(lengkapi kolom yang belum dipetakan)</span>}</p>
            <div className="grid sm:grid-cols-2 gap-3">
              {cols.map((c) => (
                <div key={c} className="flex items-center gap-2">
                  <span className="text-xs w-44 shrink-0 font-medium">{c}</span>
                  <Select value={wiz.mapping?.[c] != null ? String(wiz.mapping[c]) : "none"} onValueChange={(v) => setMap(c, v)}>
                    <SelectTrigger className="h-8 text-xs" data-testid={`wiz-map-${c}`}><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— tidak dipetakan —</SelectItem>
                      {headers.map((h, i) => <SelectItem key={i} value={String(i)}>{h || `Kolom ${i + 1}`}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
            <Button size="sm" variant="outline" className="mt-2 rounded-full" disabled={busy} onClick={() => onReanalyze(wiz.sheet, wiz.mapping)} data-testid="wiz-reanalyze">
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null} Perbarui Pratinjau
            </Button>
          </div>

          <div className="border border-border rounded-lg overflow-hidden">
            <div className="overflow-x-auto scrollbar-thin max-h-72">
              <Table>
                <TableHeader><TableRow className="bg-muted/50">
                  <TableHead className="text-xs">Baris</TableHead>
                  {cols.map((c) => <TableHead key={c} className="text-xs whitespace-nowrap">{c}</TableHead>)}
                  <TableHead className="text-xs">Status</TableHead>
                </TableRow></TableHeader>
                <TableBody>
                  {(wiz.preview || []).map((r) => (
                    <TableRow key={r.row} className={r.valid ? "" : "bg-red-500/5"}>
                      <TableCell className="text-xs tabular-nums">{r.row}</TableCell>
                      {cols.map((c) => <TableCell key={c} className="text-xs">{r.values?.[c]}</TableCell>)}
                      <TableCell className="text-xs">{r.valid ? <span className="text-emerald-600">Valid</span> : <span className="text-red-600">{r.reason}</span>}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setWiz(null)} data-testid="wiz-cancel">Batal</Button>
          <Button onClick={onConfirm} disabled={busy || wiz.valid === 0} data-testid="wiz-confirm">
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : null} Konfirmasi Import ({wiz.valid} record)
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Team({ p, reload, user }) {
  const [pimpinanList, setPimpinanList] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const canPimpinan = ["SUPER_ADMIN", "MANAGER_OPERASIONAL"].includes(user.role);

  useEffect(() => {
    if (canPimpinan) api.get("/users/assignable?role=PIMPINAN_PROYEK,MANAGER_OPERASIONAL").then((r) => setPimpinanList(r.data)).catch(() => {});
    api.get("/users/assignable?role=STAFF_OPERASIONAL,PIMPINAN_PROYEK").then((r) => setStaffList(r.data)).catch(() => {});
  }, [canPimpinan]);

  const assignP = async (uid) => { try { await api.post(`/projects/${p.id}/assign-pimpinan`, { user_id: uid }); toast.success("Pimpinan ditugaskan"); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const addStaff = async (uid) => { try { await api.post(`/projects/${p.id}/members`, { user_id: uid }); toast.success("Staff ditambahkan"); reload(); } catch (e) { toast.error(apiErr(e)); } };
  const rm = async (uid) => { try { await api.delete(`/projects/${p.id}/members/${uid}`); reload(); } catch (e) { toast.error(apiErr(e)); } };

  return (
    <div className="grid md:grid-cols-2 gap-4">
      {canPimpinan && (
        <Card className="p-5">
          <h3 className="font-heading font-semibold mb-3">Pimpinan Proyek</h3>
          <p className="text-sm mb-3">Saat ini: <span className="font-semibold">{p.pimpinan_name || "belum ditugaskan"}</span></p>
          <Select onValueChange={assignP}>
            <SelectTrigger data-testid="assign-pimpinan-select"><SelectValue placeholder="Pilih Pimpinan Proyek" /></SelectTrigger>
            <SelectContent>{pimpinanList.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
          </Select>
        </Card>
      )}
      <Card className="p-5">
        <h3 className="font-heading font-semibold mb-3 flex items-center gap-1.5"><UserPlus className="h-4 w-4" /> Staff Operasional</h3>
        <Select onValueChange={addStaff}>
          <SelectTrigger className="mb-3" data-testid="add-staff-select"><SelectValue placeholder="Tambah staff" /></SelectTrigger>
          <SelectContent>{staffList.filter((u) => !(p.staff_ids || []).includes(u.id)).map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
        </Select>
        <div className="space-y-2">
          {(p.staff || []).length === 0 && <p className="text-sm text-muted-foreground">Belum ada staff.</p>}
          {(p.staff || []).map((s) => (
            <div key={s.id} className="flex items-center justify-between rounded-lg border border-border px-3 py-2">
              <div><p className="text-sm font-medium">{s.name}</p><p className="text-xs text-muted-foreground">{s.email}</p></div>
              <Button variant="ghost" size="icon" onClick={() => rm(s.id)} data-testid={`remove-staff-${s.email}`}><Trash2 className="h-4 w-4 text-red-500" /></Button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Processing({ projectId, reload }) {
  const [docs, setDocs] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [reviewRec, setReviewRec] = useState(null);

  const load = useCallback(() => api.get(`/projects/${projectId}/documents`).then((r) => setDocs(r.data)).catch(() => {}), [projectId]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!docs) return;
    const active = docs.some((d) => ["queued", "processing"].includes(d.status));
    if (!active) return;
    const t = setInterval(load, 4000);
    return () => clearInterval(t);
  }, [docs, load]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []); if (!files.length) return;
    setUploading(true);
    for (const file of files) {
      const fd = new FormData(); fd.append("file", file);
      try {
        const { data } = await api.post(`/projects/${projectId}/documents/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
        if (data.duplicate_warning) toast.warning(`Duplikat: sama dengan "${data.duplicate_of}"`);
        else toast.success(`${file.name} diunggah`);
        await api.post(`/projects/${projectId}/documents/${data.id}/process`);
      } catch (err) { toast.error(apiErr(err)); }
    }
    setUploading(false); e.target.value = ""; load(); reload();
  };

  const process = async (docId) => { try { await api.post(`/projects/${projectId}/documents/${docId}/process`); toast.info("Pemrosesan AI dimulai"); load(); } catch (e) { toast.error(apiErr(e)); } };

  const del = async (docId) => {
    if (!window.confirm("Hapus dokumen ini secara permanen? File fisik di storage dan data OCR/AI akan dihapus total.")) return;
    try { await api.delete(`/projects/${projectId}/documents/${docId}`); toast.success("Dokumen dihapus"); load(); reload(); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const openReview = async (docId) => {
    const { data } = await api.get(`/projects/${projectId}/documents/${docId}`);
    if (data.record) setReviewRec(data.record);
    else toast.error("Record belum tersedia");
  };

  return (
    <div className="space-y-4">
             <Card className="p-5 bg-muted/30 border border-primary/20 shadow-sm">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3.5">
              <div className="p-3 rounded-xl bg-primary/10 text-primary">
                <CheckCircle2 className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-semibold text-sm">Google Drive Auto-Sync Aktif</h4>
                  <Badge variant="outline" className="text-[11px] bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 border-emerald-300">
                    Polling Otomatis (120d)
                  </Badge>
                </div>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Semua berkas scan yang dimasukkan ke subfolder Google Drive proyek ini akan diproses otomatis oleh AI (Gemini + OpenAI).
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={() => { load(); reload(); }} className="rounded-full text-xs">
                <RefreshCw className="h-3.5 w-3.5 mr-1.5" /> Segarkan Status
              </Button>
            </div>
          </div>
        </Card>

      <Card className="overflow-hidden">
        <div className="overflow-x-auto scrollbar-thin">
          <Table>
            <TableHeader><TableRow className="bg-muted/50">
              <TableHead>Dokumen</TableHead><TableHead>Diunggah oleh</TableHead><TableHead>Status</TableHead><TableHead>Aksi</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {!docs ? <TableRow><TableCell colSpan={4}><Skeleton className="h-8 w-full" /></TableCell></TableRow> :
                docs.length === 0 ? <TableRow><TableCell colSpan={4} className="text-center text-muted-foreground py-10">Belum ada dokumen.</TableCell></TableRow> :
                docs.map((d) => {
                  const st = DOC_STATUS[d.status] || DOC_STATUS.queued;
                  return (
                    <TableRow key={d.id} data-testid={`doc-row-${d.id}`}>
                      <TableCell className="font-medium text-sm">{d.filename}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{d.uploaded_by_name}</TableCell>
                      <TableCell><span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold ${st.cls}`}>{["queued", "processing"].includes(d.status) && <Loader2 className="h-3 w-3 animate-spin" />}{st.label}</span></TableCell>
                      <TableCell>
                        <div className="flex gap-1.5">
                          {d.status === "error" && <Button size="sm" variant="outline" onClick={() => process(d.id)} data-testid={`retry-${d.id}`}><Play className="h-3.5 w-3.5 mr-1" /> Ulang</Button>}
                          {d.status === "processed" && <Button size="sm" variant="outline" onClick={() => openReview(d.id)} data-testid={`review-doc-${d.id}`}><Eye className="h-3.5 w-3.5 mr-1" /> Review</Button>}
                          <Button size="sm" variant="ghost" onClick={() => del(d.id)} data-testid={`delete-doc-${d.id}`}><Trash2 className="h-3.5 w-3.5 text-red-500" /></Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </div>
      </Card>

      <Dialog open={!!reviewRec} onOpenChange={(o) => { if (!o) { setReviewRec(null); load(); reload(); } }}>
        <DialogContent className="max-w-6xl max-h-[92vh] overflow-y-auto scrollbar-thin">
          <DialogHeader><DialogTitle className="font-heading">Review & Verifikasi Arsip</DialogTitle></DialogHeader>
          {reviewRec && <ReviewPanel projectId={projectId} record={reviewRec} onChanged={() => { load(); reload(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function dl(blob, name) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a"); a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
}
